import { useEffect, useState, useCallback } from "react";
import { useNavigate, useParams } from "react-router-dom";
import api, { formatApiErrorDetail } from "../lib/api";
import { AppLayout } from "../components/AppLayout";
import { SRForm, SRReadOnly, SRStatoBadge, pickSR } from "../components/portal/SRForm";
import { Card } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Skeleton } from "../components/ui/skeleton";
import { formatDateTime } from "../lib/format";
import { toast } from "sonner";
import { Hospital, Save, HandHelping, Truck, Loader2, History, ExternalLink } from "lucide-react";

const SRLog = ({ items }) => (
  <Card className="p-5 border-slate-200">
    <h3 className="font-semibold text-slate-800 font-heading flex items-center gap-2 mb-3"><History className="h-4 w-4 text-primary" /> Registro</h3>
    <ul data-testid="sr-log" className="space-y-2">
      {items.map((l) => (
        <li key={l.id} className="text-xs text-slate-600 border-l-2 border-nexus-gold pl-3">
          <span className="font-semibold text-slate-800">{formatDateTime(l.at)}</span> · {l.by_name}
          {l.by_role === "struttura" && <span className="text-slate-400"> (struttura)</span>} — {l.details}
        </li>
      ))}
    </ul>
  </Card>
);

export default function StrutturaRequestPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [d, setD] = useState(null);
  const [form, setForm] = useState(null);
  const [busy, setBusy] = useState("");

  const load = useCallback(async () => {
    try { const { data } = await api.get(`/strutture/richieste/${id}`); setD(data); setForm(pickSR(data)); }
    catch { toast.error("Richiesta non trovata"); navigate("/operativa"); }
  }, [id, navigate]);
  useEffect(() => { load(); }, [load]);

  const run = async (key, fn) => {
    setBusy(key);
    try { await fn(); } catch (err) { toast.error("Operazione non riuscita", { description: formatApiErrorDetail(err.response?.data?.detail) }); }
    finally { setBusy(""); }
  };
  const save = () => run("save", async () => { await api.put(`/strutture/richieste/${id}`, form); toast.success("Modifiche salvate"); await load(); });
  const take = () => run("take", async () => {
    if (JSON.stringify(form) !== JSON.stringify(pickSR(d))) await api.put(`/strutture/richieste/${id}`, form);
    await api.post(`/strutture/richieste/${id}/presa-in-carico`); toast.success("Richiesta presa in carico"); await load();
  });
  const dispatch = () => run("go", async () => {
    if (JSON.stringify(form) !== JSON.stringify(pickSR(d))) await api.put(`/strutture/richieste/${id}`, form);
    navigate(`/operativa/nuova?struttura=${id}`);
  });

  if (!d || !form) return <AppLayout><Skeleton className="h-96 max-w-4xl mx-auto" /></AppLayout>;
  const editable = !d.request_id;

  return (
    <AppLayout crumbLabel={d.code}>
      <div className="max-w-4xl mx-auto pb-10 space-y-5">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div>
            <h1 data-testid="sr-detail-code" className="text-3xl font-bold font-heading text-slate-900 tracking-tight flex items-center gap-2"><Hospital className="h-7 w-7 text-primary" /> {d.code}</h1>
            <p className="text-sm text-slate-500 mt-1 flex flex-wrap items-center gap-2">
              <b className="text-slate-700">{d.struttura_nome}</b> · inviata il {formatDateTime(d.created_at)} <SRStatoBadge stato={d.stato} testid="sr-detail-stato" />
            </p>
          </div>
          <div className="flex flex-wrap gap-2">
            {editable && <Button data-testid="sr-save" variant="outline" className="gap-2" onClick={save} disabled={!!busy}>{busy === "save" ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />} Salva modifiche</Button>}
            {d.stato === "ricevuta" && <Button data-testid="sr-take" className="gap-2" onClick={take} disabled={!!busy}>{busy === "take" ? <Loader2 className="h-4 w-4 animate-spin" /> : <HandHelping className="h-4 w-4" />} Prendi in carico</Button>}
            {d.stato === "presa_in_carico" && editable && <Button data-testid="sr-dispatch" variant="gold" className="gap-2" onClick={dispatch} disabled={!!busy}><Truck className="h-4 w-4" /> Completa e invia ai partner</Button>}
            {d.request_id && <Button data-testid="sr-open-transport" variant="outline" className="gap-2" onClick={() => navigate(`/operativa/richiesta/${d.request_id}`)}><ExternalLink className="h-4 w-4" /> Trasporto {d.request_code}</Button>}
          </div>
        </div>
        <Card className="p-6 border-slate-200">
          {editable ? <SRForm value={form} onChange={setForm} /> : <SRReadOnly data={d} />}
          {d.struttura_indirizzo && !d.partenza_indirizzo && <p className="text-xs text-slate-500 mt-4">Indirizzo struttura richiedente (usato come partenza): <b>{d.struttura_indirizzo}</b></p>}
        </Card>
        <SRLog items={d.log || []} />
      </div>
    </AppLayout>
  );
}
